import { requestEmailOtp, verifyEmailCode, storeSession } from './session';

/**
 * The front door's transport, with one seam so the UI can be reviewed before
 * the GoTrue side is ready.
 *
 * Real path (default): lib/session.js talks to self-hosted GoTrue. It needs two
 * things from Azhar that do not exist yet — {{ .Token }} in the mail template,
 * and mailer_autoconfirm turned off — so until then nothing arrives by email.
 *
 * Demo path: set REACT_APP_AUTH_MOCK=true in .env.development.local to click
 * through the whole flow with no email and no backend. It exists so the design
 * can be judged, nothing else.
 *
 * Two hard rules, enforced below rather than trusted to discipline:
 *   1. It is impossible in a production build. `process.env.NODE_ENV` is
 *      inlined at build time, so the check cannot be flipped at runtime.
 *   2. The UI must say so on screen whenever it is on. See isMockAuth().
 */

const MOCK_CODE = '123456';
const MOCK_KEY = 'ihsanone_mock_otp';

export function isMockAuth() {
  return process.env.NODE_ENV !== 'production' && process.env.REACT_APP_AUTH_MOCK === 'true';
}

export const DEMO_CODE = MOCK_CODE;

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

function mockRequest({ email, data }) {
  try {
    window.sessionStorage.setItem(MOCK_KEY, JSON.stringify({ email, data }));
  } catch {
    /* private mode — the code below still works, we just cannot echo the name */
  }
  return { ok: true };
}

function mockVerify(email, code) {
  if (String(code).trim() !== MOCK_CODE) return false;
  let pending = {};
  try {
    pending = JSON.parse(window.sessionStorage.getItem(MOCK_KEY)) || {};
  } catch {
    pending = {};
  }
  const now = Date.now();
  // Same session shape storeFromGoTrue writes, so every existing caller —
  // useSession, getAccessToken, the gates — behaves exactly as it will later.
  storeSession({
    access_token: `mock.${btoa(email).replace(/=/g, '')}.token`,
    refresh_token: 'mock-refresh',
    expires_in: 3600,
    user: {
      id: `mock-${Math.abs(hash(email))}`,
      email,
      user_metadata: {
        full_name: email.split('@')[0],
        ...(pending.data || {}),
        mock: true,
        signed_in_at: new Date(now).toISOString(),
      },
    },
  });
  try {
    window.sessionStorage.removeItem(MOCK_KEY);
  } catch {
    /* ignore */
  }
  return true;
}

function hash(value) {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) {
    h = (h * 31 + value.charCodeAt(i)) | 0;
  }
  return h;
}

/** Ask for a sign-in email. Returns { ok, rateLimited?, error? }. */
export async function sendCode({ email, createUser, data }) {
  if (isMockAuth()) {
    await wait(700); // so the "sending" state is visible, as it will be for real
    return mockRequest({ email, data });
  }
  return requestEmailOtp({ email, createUser, data });
}

/** Exchange the 6-digit code for a session. Returns boolean. */
export async function submitCode(email, code) {
  if (isMockAuth()) {
    await wait(500);
    return mockVerify(email, code);
  }
  return verifyEmailCode(email, code);
}
